import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { formatAssociatedPartyAddress } from '@/features/associated-parties/formatAddress'
import { ProductManufacturerSelect } from '@/features/product-catalog/components/ProductManufacturerSelect'
import { useListAssociatedParties } from '@/shared/hooks/api/associatedParties'
import {
  useDownloadEmcProductComplianceFileOutputDocument,
  useStartEmcProductComplianceFileDeclarationOfConformityGeneration,
  type EmcProductComplianceFileOutputDocument,
  type EmcProductComplianceFileView,
} from '@/shared/hooks/api/emcComplianceFiles'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { emcProductComplianceFileDeclarationOfConformityProgressMessage } from '@/v2/lib/emcProductComplianceFileDeclarationOfConformityProgressMessage'
import { evaluateEmcProductComplianceFileDeclarationOfConformityRequirements } from '@/v2/lib/emcProductComplianceFileDeclarationOfConformityRequirements'
import { declarationOfConformityGenerateDialogContentClassName } from '@/v2/components/product-compliance-briefing/declarationOfConformityGenerateDialogContentClassName'
import { EmcProductComplianceFileOutputDocumentsSection } from './EmcProductComplianceFileOutputDocumentsSection'

const ASSOCIATED_PARTY_SELECT_ID = 'emc-doc-associated-party'

type EmcProductComplianceFileDeclarationOfConformityGenerateDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: string
  productName: string | null | undefined
  productSku: string | null | undefined
  productDescription: string | null | undefined
  emcApplies: boolean
  productFile: EmcProductComplianceFileView
  outputDocuments: EmcProductComplianceFileOutputDocument[]
  onProgressMessage: (message: string | null) => void
}

export function EmcProductComplianceFileDeclarationOfConformityGenerateDialog({
  open,
  onOpenChange,
  productId,
  productName,
  productSku,
  productDescription,
  emcApplies,
  productFile,
  outputDocuments,
  onProgressMessage,
}: EmcProductComplianceFileDeclarationOfConformityGenerateDialogProps) {
  const { t } = useTranslation('common')
  const { t: tAssociatedParties } = useTranslation('associatedParties')
  const { t: tCountries } = useTranslation('countries')
  const canWriteOrgData = useCanWriteOrgData()
  const [selectedPartyId, setSelectedPartyId] = useState('')
  const { data: associatedParties = [] } = useListAssociatedParties({
    enabled: open,
  })

  const generateMutation =
    useStartEmcProductComplianceFileDeclarationOfConformityGeneration(productId)
  const downloadMutation = useDownloadEmcProductComplianceFileOutputDocument(
    productId
  )

  useEffect(() => {
    if (!open) {
      setSelectedPartyId('')
    }
  }, [open])

  const selectedParty = useMemo(
    () => associatedParties.find((party) => party.id === selectedPartyId),
    [associatedParties, selectedPartyId]
  )

  const manufacturerPreview = useMemo(() => {
    if (selectedParty == null) {
      return null
    }
    return {
      name: selectedParty.name.trim(),
      address: formatAssociatedPartyAddress(selectedParty, {
        getCountryName: (code) => tCountries(code) || code,
        translate: (key, options) => tAssociatedParties(key, options),
      }).trim(),
    }
  }, [selectedParty, tAssociatedParties, tCountries])

  const { generateReady } =
    evaluateEmcProductComplianceFileDeclarationOfConformityRequirements({
      productName,
      productSku,
      productDescription,
      emcApplies,
      productFile,
      manufacturerName: manufacturerPreview?.name,
      manufacturerAddress: manufacturerPreview?.address,
    })

  const isBusy = generateMutation.isPending || downloadMutation.isPending
  const hasInFlightOutputDocument = outputDocuments.some(
    (document) =>
      document.status === 'PENDING' || document.status === 'PROCESSING'
  )

  const canConfirm =
    canWriteOrgData &&
    generateReady &&
    manufacturerPreview != null &&
    manufacturerPreview.name.length > 0 &&
    manufacturerPreview.address.length > 0 &&
    !isBusy &&
    !hasInFlightOutputDocument

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isBusy) {
        return
      }
      onOpenChange(nextOpen)
    },
    [isBusy, onOpenChange]
  )

  const handleConfirm = async () => {
    if (!canWriteOrgData || manufacturerPreview == null || !generateReady) {
      return
    }
    onProgressMessage(
      t('v2.pages.products.detail.emcFiles.declarationOfConformity.generating')
    )
    try {
      await generateMutation.mutateAsync({
        manufacturerName: manufacturerPreview.name,
        manufacturerAddress: manufacturerPreview.address,
        onEvent: (event) => {
          if (event.type === 'step.started') {
            const localizedMessage =
              emcProductComplianceFileDeclarationOfConformityProgressMessage(
                event.step,
                t
              )
            if (localizedMessage != null) {
              onProgressMessage(localizedMessage)
            }
          }
        },
        onComplete: async (result) => {
          await downloadMutation.mutateAsync({
            outputDocumentId: result.outputDocumentId,
            fileName: result.fileName,
          })
        },
      })
      onOpenChange(false)
    } catch {
      toast.error(
        t(
          'v2.pages.products.detail.emcFiles.declarationOfConformity.exportFailed'
        )
      )
    } finally {
      onProgressMessage(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={declarationOfConformityGenerateDialogContentClassName}
        showCloseButton={!isBusy}
        onEscapeKeyDown={(event) => {
          if (isBusy) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event) => {
          if (isBusy) {
            event.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              'v2.pages.products.detail.emcFiles.declarationOfConformity.exportDialog.title'
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'v2.pages.products.detail.emcFiles.declarationOfConformity.exportDialog.description'
            )}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          {!canWriteOrgData ? (
            <p className='text-sm text-muted-foreground'>
              {t(
                'v2.pages.products.detail.emcFiles.declarationOfConformity.readOnlyHint'
              )}
            </p>
          ) : null}
          <div className='space-y-2'>
            <Label htmlFor={ASSOCIATED_PARTY_SELECT_ID}>
              {t(
                'v2.pages.products.detail.emcFiles.declarationOfConformity.exportDialog.partyLabel'
              )}
            </Label>
            <p className='text-xs text-muted-foreground'>
              {t(
                'v2.pages.products.detail.emcFiles.declarationOfConformity.exportDialog.partyDescription'
              )}
            </p>
            <ProductManufacturerSelect
              value={selectedPartyId}
              onValueChange={setSelectedPartyId}
              disabled={isBusy || !canWriteOrgData}
              enabled={open}
              inForm={false}
              triggerId={ASSOCIATED_PARTY_SELECT_ID}
            />
          </div>
          {manufacturerPreview != null ? (
            <div className='space-y-2 rounded-md border bg-muted/30 p-3 text-sm'>
              <p className='font-medium'>
                {t(
                  'v2.pages.products.detail.emcFiles.declarationOfConformity.exportDialog.previewTitle'
                )}
              </p>
              <p>{manufacturerPreview.name}</p>
              <p className='text-muted-foreground'>
                {manufacturerPreview.address}
              </p>
            </div>
          ) : null}
          <div className='space-y-2'>
            <p className='text-sm font-medium'>
              {t(
                'v2.pages.products.detail.emcFiles.declarationOfConformity.exportDialog.issuedDocumentsTitle'
              )}
            </p>
            <EmcProductComplianceFileOutputDocumentsSection
              productId={productId}
              outputDocuments={outputDocuments}
            />
          </div>
          {hasInFlightOutputDocument ? (
            <p className='text-xs text-muted-foreground' aria-live='polite'>
              {t(
                'v2.pages.products.detail.emcFiles.outputDocuments.generatingHint'
              )}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            disabled={isBusy}
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t(
              'v2.pages.products.detail.emcFiles.declarationOfConformity.exportDialog.cancel'
            )}
          </Button>
          <Button
            type='button'
            disabled={!canConfirm}
            onClick={() => {
              void handleConfirm()
            }}
          >
            {isBusy ? <Loader2 className='size-4 animate-spin' /> : null}
            {t(
              'v2.pages.products.detail.emcFiles.declarationOfConformity.exportDialog.confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
